"use client";

import { useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { Activity, AppWindow, Boxes, MoreVertical, Plus, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EnterpriseContextMenu } from "./enterprise-context-menu";
import { publishRunyiValueChange } from "../lib/runyi-value-sync";

export type InfrastructureActionTarget = {
  target_entity_key: string;
  display_name: string;
  category_key: string;
  provider_key?: string | null;
  logical_capability?: string | null;
  target_kind: "INSTALLED" | "CAPABILITY" | "PROVIDER_RESOURCE";
  environment_key?: string | null;
  metadata?: Record<string, unknown>;
  provider_account_identity?: string | null;
  identity_email?: string | null;
  identity_email_type?: string | null;
  identity_email_verified?: boolean | null;
  identity_email_source?: string | null;
  sync_state?: string | null;
  last_synced_at?: string | null;
  provider_project_ref?: string | null;
  region_ref?: string | null;
  lifecycle_state?: string | null;
  health_state?: string | null;
  verification_state?: string | null;
  capabilities?: string[];
};

export type InfrastructureActionAssignment = {
  assignment_id: string;
  application_entity_key: string;
  assignment_slot: string;
  assignment_role: string;
  target_entity_key: string;
  category_key?: string | null;
  environment_key: string;
  state: string;
};

type ApplicationItem = { entity_key: string; display_name: string };

export type AddInfrastructureIntent = {
  source: string;
  provider_capability_key?: string | null;
  provider_key?: string | null;
  category_key?: string | null;
  target_entity_key?: string | null;
  logical_capability?: string | null;
  environment_key?: string | null;
  application_entity_key?: string | null;
  project_id?: string | null;
  mode?: "NEW" | "EXISTING";
  target_scope?: "APPLICATION" | "PROJECT" | null;
  source_display_name?: string | null;
  provider_account_identity?: string | null;
  provider_project_ref?: string | null;
  application_display_name?: string | null;
  project_display_name?: string | null;
};

function tone(value: string) {
  const state = value.toUpperCase();
  if (state === "ACTIVE") return "bg-emerald-400/10 text-emerald-600 dark:text-emerald-300";
  if (["STANDBY", "AVAILABLE", "DESIRED"].includes(state)) return "bg-amber-400/10 text-amber-700 dark:text-amber-200";
  if (["DISABLED", "RETIRED"].includes(state)) return "bg-foreground/[0.06] text-foreground/55";
  if (["DEGRADED", "FAILED"].includes(state)) return "bg-red-400/10 text-red-600 dark:text-red-300";
  return "bg-foreground/[0.06] text-foreground/55";
}

async function functionError(error: unknown) {
  const fallback = error instanceof Error ? error.message : "Infrastructure operation failed.";
  const response = (error as { context?: Response } | null)?.context;
  if (!(response instanceof Response)) return fallback;
  try {
    const payload = await response.clone().json() as Record<string, unknown>;
    return String(payload.detail ?? payload.error ?? payload.message ?? fallback);
  } catch {
    return `${fallback} (HTTP ${response.status})`;
  }
}

export function dispatchAddInfrastructure(intent: AddInfrastructureIntent) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem("runyi:add-infrastructure:intent",JSON.stringify(intent));
  } catch {
    // Session storage is an optimization only; the event remains authoritative for this render.
  }
  window.dispatchEvent(new CustomEvent("runyi:add-infrastructure",{detail:intent}));
}

function intentForTarget(target: InfrastructureActionTarget, targetScope?: "APPLICATION" | "PROJECT"): AddInfrastructureIntent {
  const metadataCapability = typeof target.metadata?.provider_capability_key === "string"
    ? String(target.metadata.provider_capability_key)
    : null;
  const directCapability = target.target_kind === "CAPABILITY"
    ? target.target_entity_key.replace(/^PROVIDER_CAPABILITY:/,"")
    : null;
  return {
    source:"INFRASTRUCTURE_ACTION_MENU",
    provider_capability_key:directCapability || metadataCapability,
    provider_key:target.provider_key ?? null,
    category_key:target.category_key,
    target_entity_key:target.target_kind === "CAPABILITY"
      ? null
      : target.target_entity_key.replace(/^RESOURCE:/,""),
    logical_capability:target.logical_capability ?? null,
    environment_key:target.environment_key ?? "PRODUCTION",
    mode:target.target_kind === "PROVIDER_RESOURCE" ? "EXISTING" : "NEW",
    target_scope:targetScope ?? null,
    source_display_name:target.display_name,
    provider_account_identity:target.provider_account_identity ?? null,
    provider_project_ref:target.provider_project_ref ?? null,
  };
}

export function UniversalInfrastructureActions({
  supabase,
  connected,
  target,
  applications,
  assignments = [],
  onChanged,
}: {
  supabase: SupabaseClient | null;
  connected: boolean;
  target: InfrastructureActionTarget;
  applications: ApplicationItem[];
  assignments?: InfrastructureActionAssignment[];
  onChanged?: () => void | Promise<void>;
}) {
  const [menuOpen,setMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const [menuDirection,setMenuDirection] = useState<"up" | "down">("down");
  const [manageOpen,setManageOpen] = useState(false);
  const [busy,setBusy] = useState<string | null>(null);
  const [notice,setNotice] = useState<string | null>(null);

  function toggleMenu() {
    if (!menuOpen && menuButtonRef.current && typeof window !== "undefined") {
      const rect = menuButtonRef.current.getBoundingClientRect();
      const viewport = window.visualViewport;
      const viewportBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
      const reservedBottom = 72;
      const estimatedMenuHeight = 150;
      setMenuDirection(rect.bottom + estimatedMenuHeight > viewportBottom - reservedBottom ? "up" : "down");
    }
    setMenuOpen((value) => !value);
  }

  function openUniversalAdd(targetScope?: "APPLICATION" | "PROJECT") {
    setMenuOpen(false);
    dispatchAddInfrastructure(intentForTarget(target,targetScope));
  }

  async function transition(assignment: InfrastructureActionAssignment, state: "ACTIVE" | "STANDBY" | "DISABLED" | "RETIRED") {
    if (!supabase || !connected) return;
    if (state === "RETIRED" && !window.confirm("Remove this infrastructure assignment from the application? The provider resource will NOT be destroyed.")) return;
    setBusy(`${assignment.assignment_id}:${state}`);
    setNotice(null);
    try {
      const { data,error } = await supabase.functions.invoke("runyi-infrastructure-actions",{
        body:{
          operation:"enterprise_transition_resource_assignment",
          assignment_id:assignment.assignment_id,
          new_state:state,
          reason:state === "RETIRED"
            ? "Owner removed infrastructure from the application composition. Provider resource retained."
            : `Owner changed application infrastructure assignment to ${state}.`,
        },
      });
      if (error) throw new Error(await functionError(error));
      if (data?.error) throw new Error(String(data.error));
      setNotice(state === "ACTIVE"
        ? "Infrastructure is ACTIVE. Any previous ACTIVE resource in the same environment/category/role was moved to STANDBY."
        : `Infrastructure state changed to ${state}.`);
      publishRunyiValueChange(["applications", "infrastructure", "catalogue"]);
      await onChanged?.();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Infrastructure state transition failed.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="relative shrink-0" onClick={(event) => event.stopPropagation()}>
      <EnterpriseContextMenu compact label={`Infrastructure actions for ${target.display_name}`} title="Infrastructure actions">
        <button type="button" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs hover:bg-foreground/[0.05]" onClick={() => openUniversalAdd("APPLICATION")}>
          <AppWindow className="size-3.5" />Add to App
        </button>
        <button type="button" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs hover:bg-foreground/[0.05]" onClick={() => openUniversalAdd("PROJECT")}>
          <Boxes className="size-3.5" />Add to Project
        </button>
        {assignments.length > 0 && <button type="button" className="w-full rounded-lg px-3 py-2 text-left text-xs hover:bg-foreground/[0.05]" onClick={() => setManageOpen(true)}>
          Active / standby / remove…
        </button>}
        <button type="button" className="w-full rounded-lg px-3 py-2 text-left text-xs text-foreground/60 hover:bg-foreground/[0.05]" onClick={() => setManageOpen(true)}>
          Open infrastructure
        </button>
      </EnterpriseContextMenu>

      {manageOpen && (
        <div className="fixed inset-0 z-[140] grid place-items-center bg-black/45 p-3" onClick={() => setManageOpen(false)}>
          <div className="max-h-[82vh] w-full max-w-xl overflow-y-auto rounded-2xl border border-foreground/10 bg-background p-4 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">Infrastructure controls</p>
                <p className="mt-1 break-all font-mono text-[9px] text-foreground/45">{target.target_entity_key}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setManageOpen(false)}><X className="size-4" /></Button>
            </div>

            <div className="mt-3 grid gap-2">
              <div className="rounded-xl bg-foreground/[0.025] p-3 text-xs">
                <p><strong>Name:</strong> {target.display_name}</p>
                <p className="mt-1"><strong>Category:</strong> {target.category_key}</p>
                <p className="mt-1"><strong>Provider:</strong> {target.provider_key || "RUNYI / unresolved"}</p>
                <p className="mt-1"><strong>Identity email:</strong> {target.identity_email || target.provider_account_identity || "IDENTITY PENDING"}</p>
                <p className="mt-1"><strong>Identity:</strong> {target.identity_email_type || (target.provider_account_identity ? "PROVIDER_ACCOUNT" : "PENDING")} · {target.identity_email_verified ? "VERIFIED" : "PENDING"}</p>
                {target.identity_email_source ? <p className="mt-1 break-all"><strong>Identity source:</strong> {target.identity_email_source}</p> : null}
                <p className="mt-1"><strong>Sync:</strong> {target.sync_state || "UNKNOWN"}{target.last_synced_at ? ` · ${new Date(target.last_synced_at).toLocaleString()}` : ""}</p>
                <p className="mt-1"><strong>Provider project/resource:</strong> {target.provider_project_ref || "Unavailable"}</p>
                <p className="mt-1"><strong>Region:</strong> {target.region_ref || "Unavailable"}</p>
                <p className="mt-1"><strong>Environment:</strong> {target.environment_key || "PRODUCTION"}</p>
                <p className="mt-1"><strong>Lifecycle:</strong> {target.lifecycle_state || "Unavailable"}</p>
                <p className="mt-1"><strong>Health:</strong> {target.health_state || "Unavailable"}</p>
                <p className="mt-1"><strong>Verification:</strong> {target.verification_state || "Unavailable"}</p>
                <p className="mt-1"><strong>Type:</strong> {target.target_kind}</p>
                {target.capabilities?.length ? <p className="mt-1"><strong>Capabilities:</strong> {target.capabilities.join(", ")}</p> : null}
              </div>

              <div className="flex items-center justify-between gap-2 px-1 pt-1">
                <p className="text-[10px] font-bold uppercase tracking-wider text-foreground/40">Applications using this infrastructure</p>
                <Badge className="bg-foreground/[0.05] text-foreground/55">{assignments.length}</Badge>
              </div>

              {assignments.length === 0 && (
                <div className="rounded-xl border border-dashed border-foreground/12 p-4">
                  <p className="text-xs text-foreground/45">This infrastructure is not currently assigned to an application.</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button type="button" variant="outline" size="sm" className="rounded-xl" onClick={() => {setManageOpen(false);openUniversalAdd("APPLICATION");}}>
                      <AppWindow className="size-3.5" />Add to App
                    </Button>
                    <Button type="button" variant="outline" size="sm" className="rounded-xl" onClick={() => {setManageOpen(false);openUniversalAdd("PROJECT");}}>
                      <Boxes className="size-3.5" />Add to Project
                    </Button>
                  </div>
                </div>
              )}

              {assignments.map((assignment) => (
                <div key={assignment.assignment_id} className="rounded-xl border border-foreground/8 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-xs font-semibold">{applications.find((item) => item.entity_key === assignment.application_entity_key)?.display_name ?? assignment.application_entity_key}</p>
                      <p className="mt-1 text-[9px] text-foreground/45">{assignment.environment_key} · {assignment.category_key || target.category_key} · {assignment.assignment_role}</p>
                    </div>
                    <Badge className={tone(assignment.state)}>{assignment.state}</Badge>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {assignment.state !== "ACTIVE" && <Button size="sm" variant="outline" onClick={() => void transition(assignment,"ACTIVE")} disabled={busy?.startsWith(assignment.assignment_id)}><Activity className="mr-1 size-3.5" />Set active</Button>}
                    {assignment.state === "ACTIVE" && <Button size="sm" variant="outline" onClick={() => void transition(assignment,"STANDBY")} disabled={busy?.startsWith(assignment.assignment_id)}>Deactivate → Standby</Button>}
                    {assignment.state !== "DISABLED" && <Button size="sm" variant="outline" onClick={() => void transition(assignment,"DISABLED")} disabled={busy?.startsWith(assignment.assignment_id)}>Disable</Button>}
                    <Button size="sm" variant="outline" onClick={() => void transition(assignment,"RETIRED")} disabled={busy?.startsWith(assignment.assignment_id)}>Remove from application</Button>
                  </div>
                </div>
              ))}
            </div>

            {notice && <p className="mt-3 rounded-xl bg-foreground/[0.04] px-3 py-2 text-xs text-foreground/70">{notice}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export function ApplicationInfrastructureMenu({
  applicationEntityKey,
  applicationDisplayName,
  onOpen,
  onManageInfrastructure,
  onEditConfiguration,
  onOpenTest,
  onOpenProjects,
}: {
  applicationEntityKey:string;
  applicationDisplayName?:string;
  onOpen?:()=>void;
  onManageInfrastructure?:()=>void;
  onEditConfiguration?:()=>void;
  onOpenTest?:()=>void;
  onOpenProjects?:()=>void;
}) {
  const [open,setOpen] = useState(false);

  function addInfrastructure() {
    setOpen(false);
    dispatchAddInfrastructure({
      source:"APPLICATION_MENU",
      application_entity_key:applicationEntityKey,
      environment_key:"PRODUCTION",
      mode:"NEW",
      target_scope:"APPLICATION",
      application_display_name:applicationDisplayName ?? applicationEntityKey,
    });
  }

  return (
    <div className="relative shrink-0" onClick={(event) => event.stopPropagation()}>
      <EnterpriseContextMenu compact label={`Application actions for ${applicationDisplayName ?? applicationEntityKey}`} title="Application OS">
          <button type="button" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs hover:bg-foreground/[0.05]" onClick={() => {setOpen(false);onOpen?.();}}><AppWindow className="size-3.5" />Open application details</button>
          <button type="button" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs hover:bg-foreground/[0.05]" onClick={() => {setOpen(false);onOpenTest?.();}}><AppWindow className="size-3.5" />Open / Test Application</button>
          <button type="button" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs hover:bg-foreground/[0.05]" onClick={() => {setOpen(false);onEditConfiguration?.();}}><Boxes className="size-3.5" />Edit Configuration</button>
          <button type="button" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs hover:bg-foreground/[0.05]" onClick={addInfrastructure}><Plus className="size-3.5" />Add Infrastructure</button>
          <button type="button" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs hover:bg-foreground/[0.05]" onClick={() => {setOpen(false);onManageInfrastructure?.();}}><Boxes className="size-3.5" />AI providers &amp; models</button>
          <button type="button" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs hover:bg-foreground/[0.05]" onClick={() => {setOpen(false);onManageInfrastructure?.();}}><Boxes className="size-3.5" />Manage / remove infrastructure</button>
          <button type="button" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs hover:bg-foreground/[0.05]" onClick={() => {setOpen(false);onOpenProjects?.();}}><Boxes className="size-3.5" />Infrastructure Projects</button>
          <button type="button" className="w-full rounded-lg px-3 py-2 text-left text-xs text-foreground/55 hover:bg-foreground/[0.05]" onClick={() => {setOpen(false);onOpen?.();}}>Health · usage · deployments</button>
      </EnterpriseContextMenu>
    </div>
  );
}
